import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as THREE from 'three';
import { Starfield } from './starfield';
import { Navigation } from './components/layout/Navigation';
import { LoadingScreen } from './components/layout/LoadingScreen';
import { SectionIndicators } from './components/layout/SectionIndicators';
import { MainSections } from './sections/MainSections';
import SplashCursor from './components/ui/SpashCursor';
import audioPlayer from './services/audioPlayer';
import { createStream } from './services/pkiApi';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://v2.schmalgsicht.de';

// Configuration constants
const TRACK_POLLING_INTERVAL = 5000;    // Track title update interval (ms)
const STATUS_CHECK_INTERVAL = 30000;    // Stream status check interval (ms)
const DEFAULT_VOLUME = 0.7;             // Default audio volume (0-1)
const SCROLL_COMPACT_THRESHOLD = 0.2;   // Scroll progress threshold for compact button

export default function Apple3DScroll() {
  const containerRef = useRef(null);
  const starfieldRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const isAnalyzerActiveRef = useRef(false);

  const [currentSection, setCurrentSection] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isAnalyzerActive, setIsAnalyzerActive] = useState(false);
  const [isStreamLoading, setIsStreamLoading] = useState(false);
  const [mode, setMode] = useState('KI'); // 'KI' or 'PKI'
  const [currentTrack, setCurrentTrack] = useState('');
  const [isPKIActive, setIsPKIActive] = useState(false);
  const [pkiCurrentQuestion, setPKICurrentQuestion] = useState('');
  const [pkiCompleted, setPkiCompleted] = useState(false); // Track if PKI questions were completed
  const [volume, setVolume] = useState(DEFAULT_VOLUME);
  const backgroundColor = '#0a0a0a';
  const [currentProfileId, setCurrentProfileId] = useState(null); // Listener Profile ID
  const [isStreamOffline, setIsStreamOffline] = useState(false); // Stream status from API

  // Helper: Set Media Session metadata immediately (needed for iOS to recognize as media)
  const setMediaSessionMetadata = (title) => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: title,
        artist: 'schmalgsicht.fm',
        album: 'Neuer. Besser. Ich.',
        artwork: [
          { src: '/schmalgsicht_logo.png', sizes: '512x512', type: 'image/png' }
        ]
      });
      navigator.mediaSession.playbackState = 'playing';
    }
  };

  // Stable button state - only changes when crossing thresholds
  const isButtonCompact = useMemo(() => {
    return scrollProgress > SCROLL_COMPACT_THRESHOLD || currentSection >= 1;
  }, [scrollProgress, currentSection]);

  // Initialize 3D scene (Starfield only)
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Prevent double initialization
    if (sceneRef.current) {
      return;
    }

    // Create scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Create camera
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 5;
    cameraRef.current = camera;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create starfield
    const starfield = new Starfield(scene);
    starfieldRef.current = starfield;

    // Mark loading as complete
    setIsLoading(false);

    // Animation loop
    let animationId;
    const animate = () => {
      animationId = requestAnimationFrame(animate);

      // Update starfield with scroll position for parallax effect
      if (starfieldRef.current) {
        starfieldRef.current.update(window.scrollY);
      }

      // Render scene
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationId);

      if (container && renderer?.domElement) {
        try {
          container.removeChild(renderer.domElement);
        } catch (e) {
          // Silent cleanup
        }
      }

      renderer.dispose();
      sceneRef.current = null;
      rendererRef.current = null;
      cameraRef.current = null;
      starfieldRef.current = null;
    };
  }, []);

  // Handle scroll with throttling for performance
  useEffect(() => {
    let ticking = false;
    let lastScrollY = 0;

    const updateScroll = () => {
      const scrollY = lastScrollY;
      const sectionHeight = window.innerHeight;
      // Switch earlier - when new section is ~20% visible
      const newSection = Math.floor((scrollY + sectionHeight * 0.2) / sectionHeight);

      // Calculate scroll progress within current section
      const sectionProgress = (scrollY % sectionHeight) / sectionHeight;

      setCurrentSection(newSection);
      setScrollProgress(sectionProgress);

      // Keep stars white
      if (starfieldRef.current) {
        starfieldRef.current.setStarColor(0xffffff);
      }

      ticking = false;
    };

    const handleScroll = () => {
      lastScrollY = window.scrollY;
      if (!ticking) {
        requestAnimationFrame(updateScroll);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  // Fetch and update track title from Icecast API
  useEffect(() => {
    if (!isAnalyzerActive) return;

    const fetchTrackTitle = async () => {
      try {
        const response = await fetch('https://v2.schmalgsicht.de/conv2');
        if (response.ok) {
          const data = await response.json();
          const sources = data?.icestats?.source;

          // sources can be an array (multiple streams) or single object
          const sourceArray = Array.isArray(sources) ? sources : (sources ? [sources] : []);

          let title = 'Schmalgsicht FM';

          if (isPKIActive && currentProfileId) {
            // For PKI: Find the stream matching our profile ID
            const pkiSource = sourceArray.find(s =>
              s.listenurl?.includes(currentProfileId) ||
              s.server_name?.includes(currentProfileId)
            );
            title = pkiSource?.title || 'Dein personalisierter Stream';
          } else {
            // For KI: Find the main /radio stream explicitly
            const kiSource = sourceArray.find(s => s.listenurl?.includes('/radio'));
            title = kiSource?.title || sourceArray[0]?.title || 'Schmalgsicht FM';
          }

          setCurrentTrack(prev => prev === title ? prev : title);
          if (isPKIActive) {
            setPKICurrentQuestion(prev => prev === title ? prev : title);
          }
        } else {
          const fallback = isPKIActive ? 'Dein personalisierter Stream' : 'Schmalgsicht FM';
          setCurrentTrack(prev => prev === fallback ? prev : fallback);
        }
      } catch (error) {
        const fallback = isPKIActive ? 'Dein personalisierter Stream' : 'Schmalgsicht FM';
        setCurrentTrack(prev => prev === fallback ? prev : fallback);
      }
    };

    fetchTrackTitle();
    const interval = setInterval(fetchTrackTitle, TRACK_POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [isAnalyzerActive, isPKIActive, currentProfileId]);

  // Update Media Session metadata when track changes (shows in system media controls)
  useEffect(() => {
    if ('mediaSession' in navigator && currentTrack && isAnalyzerActive) {
      navigator.mediaSession.metadata = new MediaMetadata({
        title: currentTrack,
        artist: 'schmalgsicht.fm',
        album: 'Neuer. Besser. Ich.',
        artwork: [
          { src: '/schmalgsicht_logo.png', sizes: '512x512', type: 'image/png' }
        ]
      });

      // Set playback state for live stream (hides time display on most devices)
      navigator.mediaSession.playbackState = 'playing';

    }
  }, [currentTrack, isAnalyzerActive]);

  // Update playback state when stream stops
  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = isAnalyzerActive ? 'playing' : 'paused';
    }
  }, [isAnalyzerActive]);

  // Media Session action handlers (for system media controls like iPhone Control Center)
  useEffect(() => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.setActionHandler('play', () => {
        if (!isAnalyzerActive) {
          setCurrentTrack('Stream wird geladen ...');
          setMediaSessionMetadata('Stream wird geladen ...');
          audioPlayer.play();
          setIsAnalyzerActive(true);
          isAnalyzerActiveRef.current = true;
        }
      });

      navigator.mediaSession.setActionHandler('pause', () => {
        if (isAnalyzerActive) {
          audioPlayer.pause();
          setIsAnalyzerActive(false);
          isAnalyzerActiveRef.current = false;
        }
      });

      navigator.mediaSession.setActionHandler('stop', () => {
        audioPlayer.pause();
        setIsAnalyzerActive(false);
        isAnalyzerActiveRef.current = false;
      });
    }

    return () => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.setActionHandler('play', null);
        navigator.mediaSession.setActionHandler('pause', null);
        navigator.mediaSession.setActionHandler('stop', null);
      }
    };
  }, [isAnalyzerActive]);

  // Update audioPlayer volume when volume state changes
  useEffect(() => {
    audioPlayer.setVolume(volume);
  }, [volume]);

  // Check stream status from API
  useEffect(() => {
    const checkStreamStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/status`);
        if (response.ok) {
          const data = await response.json();
          setIsStreamOffline(data.stream_online !== true);
        } else {
          setIsStreamOffline(true);
        }
      } catch (error) {
        setIsStreamOffline(true);
      }
    };

    checkStreamStatus();
    const statusInterval = setInterval(checkStreamStatus, STATUS_CHECK_INTERVAL);

    return () => clearInterval(statusInterval);
  }, []);

  const handlePKIQuestionChange = (question) => {
    setPKICurrentQuestion(question);
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);

    // Activate PKI selection when switching to PKI mode
    if (newMode === 'PKI') {
      // Set initial question
      setPKICurrentQuestion('Wie fühlst du dich?');

      setIsPKIActive(true);
      setPkiCompleted(false); // Reset PKI completion status

      // Stop stream and analyzer if active
      if (isAnalyzerActive) {
        audioPlayer.pause();
        setIsAnalyzerActive(false);
        isAnalyzerActiveRef.current = false;
        setIsStreamLoading(false);
        setCurrentTrack('');
      }
    } else if (newMode === 'KI') {
      // Deactivate PKI mode when switching back to KI
      setIsPKIActive(false);
      setPkiCompleted(false); // Reset PKI completion status

      // Stop stream and analyzer if active
      if (isAnalyzerActive) {
        audioPlayer.pause();
        setIsAnalyzerActive(false);
        isAnalyzerActiveRef.current = false;
        setIsStreamLoading(false);
        setCurrentTrack('');
      }
    }
  };

  // Handle joining a shared stream by profile ID
  const handleJoinStream = async (profileId) => {
    if (!profileId) return;

    // Switch to PKI mode
    setMode('PKI');
    setIsPKIActive(true);
    setPkiCompleted(true);
    setCurrentProfileId(profileId);
    setIsStreamLoading(true);
    setCurrentTrack('Stream wird geladen ...');
    setMediaSessionMetadata('Stream wird geladen ...');

    try {
      const streamUrl = `https://v2.schmalgsicht.de/pkistream/${profileId}`;

      audioPlayer.setVolume(volume);
      await audioPlayer.switchStream(streamUrl);

      setIsAnalyzerActive(true);
      isAnalyzerActiveRef.current = true;
      setIsStreamLoading(false);
      setCurrentTrack('Geteilter Stream');
      setPKICurrentQuestion('Geteilter Stream');
    } catch (error) {
      setIsStreamLoading(false);
      setPKICurrentQuestion('Stream konnte nicht gefunden werden');
    }
  };

  const handlePKIComplete = async (data) => {
    // Mark PKI as completed
    setPkiCompleted(true);

    // Speichere Profile ID
    if (data.profileId) {
      setCurrentProfileId(data.profileId);
    }

    // Start loading
    setIsStreamLoading(true);
    setCurrentTrack('Stream wird geladen ...');
    setMediaSessionMetadata('Stream wird geladen ...');

    try {
      // PKI Mode: Wait for personalized stream URL
      if (data.profileId) {
        // DUMMY MODE: If profileId starts with 'test_', just play the main KI stream
        if (data.profileId.startsWith('test_')) {
          audioPlayer.setVolume(volume);
          await audioPlayer.switchStream('https://v2.schmalgsicht.de/stream/');

          setIsAnalyzerActive(true);
          isAnalyzerActiveRef.current = true;
          setIsStreamLoading(false);
          setCurrentTrack('Test-Stream (Dummy Mode)');
          setPKICurrentQuestion('Test-Stream (Dummy Mode)');
        } else {
          const response = await createStream(data.profileId);

          if (response.stream_url) {
            // Set the personalized stream URL and start playing
            audioPlayer.setVolume(volume);
            await audioPlayer.switchStream(response.stream_url);

            setIsAnalyzerActive(true);
            isAnalyzerActiveRef.current = true;
            setIsStreamLoading(false);
            setCurrentTrack('Dein personalisierter Stream');
            setPKICurrentQuestion('Dein personalisierter Stream');
          } else {
            throw new Error('No stream_url in response');
          }
        }
      } else {
        throw new Error('No profileId available');
      }

    } catch (error) {
      setIsStreamLoading(false);
      setPKICurrentQuestion('Stream konnte nicht gestartet werden');
    }
  };

  // Persistent Player handlers
  const handlePlayerPlayPause = async () => {
    // Toggle analyzer active state
    if (isAnalyzerActive) {
      // Stop stream
      audioPlayer.pause();
      setIsAnalyzerActive(false);
      isAnalyzerActiveRef.current = false;
      setIsStreamLoading(false);
      setCurrentTrack('');

      // Show "Klicke Play" message if in PKI mode
      if (isPKIActive) {
        setPKICurrentQuestion(
          <span className="flex items-center gap-2">
            Klicke
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="relative z-10">
              <path d="M8 5v14l11-7z" fill="black"/>
            </svg>
            um zu starten
          </span>
        );
      }
    } else {
      // Start stream
      setIsStreamLoading(true);
      setCurrentTrack('Stream wird geladen ...');
      setMediaSessionMetadata('Stream wird geladen ...');
      try {
        audioPlayer.setVolume(volume); // Set initial volume
        await audioPlayer.play();
        setIsAnalyzerActive(true);
        isAnalyzerActiveRef.current = true;
        setIsStreamLoading(false);
        // Track title is fetched automatically by useEffect when isAnalyzerActive becomes true
      } catch (error) {
        setIsStreamLoading(false);
      }
    }
  };


  const handlePlayerVolumeChange = (newVolume) => {
    setVolume(newVolume);
  };

  return (
    <div className="relative w-full" style={{ backgroundColor }}>
      <LoadingScreen isLoading={isLoading} />
      <Navigation
        showPlayer={isAnalyzerActive}
        isPlaying={isAnalyzerActive}
        onPlayPause={handlePlayerPlayPause}
        volume={volume}
        onVolumeChange={handlePlayerVolumeChange}
        currentTrack={currentTrack}
        currentSection={currentSection}
      />

      {/* Fixed background */}
      <div className="fixed inset-0 z-0" style={{ backgroundColor }} />

      {/* SplashCursor - always mounted, visibility via CSS */}
      <SplashCursor visible={currentSection === 0} />

      {/* 3D Canvas Container - always visible for starfield */}
      <div
        ref={containerRef}
        className="w-full h-screen z-10"
        style={{
          pointerEvents: 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          display: 'block'
        }}
      />

      {/* Main content */}
      <div className="relative z-20">
        <MainSections
          currentSection={currentSection}
          scrollProgress={scrollProgress}
          isAnalyzerActive={isAnalyzerActive}
          isStreamLoading={isStreamLoading}
          isStreamOffline={isStreamOffline}
          currentTrack={currentTrack}
          isPKIActive={isPKIActive}
          pkiCurrentQuestion={pkiCurrentQuestion}
          pkiCompleted={pkiCompleted}
          currentProfileId={currentProfileId}
          mode={mode}
          onModeChange={handleModeChange}
          onPlayClick={handlePlayerPlayPause}
          onPKIComplete={handlePKIComplete}
          onPKIQuestionChange={handlePKIQuestionChange}
          onJoinStream={handleJoinStream}
          backgroundColor={backgroundColor}
        />
      </div>

      {/* Section Indicators - Hidden on mobile */}
      <div className="hidden md:block">
        <SectionIndicators currentSection={currentSection} />
      </div>

      {/* Scroll/Back to top button - flies to corner and shrinks when scrolling */}
      <button
        onClick={() => {
          window.scrollTo({
            top: isButtonCompact ? 0 : window.innerHeight,
            behavior: 'smooth'
          });
        }}
        className="block fixed bottom-6 sm:bottom-8 rounded-full font-medium pointer-events-auto overflow-hidden text-base bg-white text-black"
        style={{
          zIndex: 100,
          left: isButtonCompact ? '1rem' : '50%',
          transform: isButtonCompact ? 'translateX(0) translateZ(0)' : 'translateX(-50%) translateZ(0)',
          padding: isButtonCompact ? '0.75rem' : window.innerWidth < 640 ? '1rem 2rem' : '0.75rem 2rem',
          transition: 'left 1.2s cubic-bezier(0.19, 1, 0.22, 1), transform 1.2s cubic-bezier(0.19, 1, 0.22, 1), padding 1.2s cubic-bezier(0.19, 1, 0.22, 1)',
          willChange: 'left, transform, padding',
          backfaceVisibility: 'hidden',
          WebkitFontSmoothing: 'antialiased'
        }}
      >
        <span
          style={{
            opacity: isButtonCompact ? 0 : 1,
            display: isButtonCompact ? 'none' : 'inline',
            transition: 'opacity 0.8s cubic-bezier(0.19, 1, 0.22, 1)'
          }}
        >
          Probier es aus
        </span>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="text-black"
          style={{
            opacity: isButtonCompact ? 1 : 0,
            display: isButtonCompact ? 'inline-block' : 'none',
            transition: 'opacity 0.8s cubic-bezier(0.19, 1, 0.22, 1)'
          }}
        >
          <path
            d="M12 19V5M12 5L5 12M12 5L19 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
}
